import { importGoogleMapsLibrary, type GoogleMapsLibraryName } from '@/lib/google-maps-loader'
import { useEffect, useState } from 'react'

const DEFAULT_LIBRARIES: GoogleMapsLibraryName[] = ['maps', 'geometry']

/** The loaded libraries; one not requested stays null. */
interface GoogleMapsState {
  maps: google.maps.MapsLibrary | null
  geometry: google.maps.GeometryLibrary | null
  marker: google.maps.MarkerLibrary | null
  geocoding: google.maps.GeocodingLibrary | null
  routes: google.maps.RoutesLibrary | null
  isLoaded: boolean
  /** Whatever the loader rejected with. */
  error: unknown
}

const initialState: GoogleMapsState = {
  maps: null,
  geometry: null,
  marker: null,
  geocoding: null,
  routes: null,
  isLoaded: false,
  error: null,
}

export function useGoogleMaps(
  libraries: readonly GoogleMapsLibraryName[] = DEFAULT_LIBRARIES
): GoogleMapsState {
  const [state, setState] = useState(initialState)
  const librariesKey = libraries.join(',')

  useEffect(() => {
    let isCancelled = false
    const requestedLibraries = new Set(librariesKey.split(',').filter(Boolean))

    const loadLibraries = async () => {
      try {
        setState((current) => ({
          ...current,
          isLoaded: false,
          error: null,
        }))

        const [mapsLib, geometryLib, markerLib, geocodingLib, routesLib] = await Promise.all([
          importGoogleMapsLibrary('maps'),
          requestedLibraries.has('geometry') ? importGoogleMapsLibrary('geometry') : null,
          requestedLibraries.has('marker') ? importGoogleMapsLibrary('marker') : null,
          requestedLibraries.has('geocoding') ? importGoogleMapsLibrary('geocoding') : null,
          requestedLibraries.has('routes') ? importGoogleMapsLibrary('routes') : null,
        ])

        if (isCancelled) {
          return
        }

        setState({
          maps: mapsLib,
          geometry: geometryLib,
          marker: markerLib,
          geocoding: geocodingLib,
          routes: routesLib,
          isLoaded: true,
          error: null,
        })
      } catch (err) {
        if (isCancelled) {
          return
        }

        setState((current) => ({
          ...current,
          isLoaded: false,
          error: err,
        }))
      }
    }

    void loadLibraries()

    return () => {
      isCancelled = true
    }
  }, [librariesKey])

  return state
}
